import { PeriodSelector } from "@/components/dashboard-sales/PeriodSelector";
import { Skeleton } from "@/components/ui/skeleton";
import { usePeriodFilter } from "@/contexts/dashboard-filter-context";
import { useLeadStats } from "@/hooks/use-lead-stats";
import { useUsers } from "@/hooks/use-users";
import { formatPercent } from "@/lib/format";
import { answerRate, meetingsPerCall, summarizeByUser, type ActivityTotals } from "@/lib/lead-stats-calc";

const pct = (v: number | null) => (v === null ? "–" : formatPercent(Math.round(v * 100)));

function Cells({ t }: { t: ActivityTotals }) {
  return (
    <>
      <td className="p-2 text-right tabular-nums">{t.calls}</td>
      <td className="p-2 text-right tabular-nums">{t.answered}</td>
      <td className="p-2 text-right tabular-nums">{pct(answerRate(t))}</td>
      <td className="p-2 text-right tabular-nums">{t.meetings_booked}</td>
      <td className="p-2 text-right tabular-nums">{pct(meetingsPerCall(t))}</td>
      <td className="p-2 text-right tabular-nums">{t.meetings_held}</td>
    </>
  );
}

/**
 * Ringeaktivitet for DL (CRM-4.7): per møtebooker for i dag, denne uken og valgt periode. Ringt-andel og
 * utbytte per liste står i Leadlister-tabellen. Må ligge i DashboardFilterProvider (periodevelgeren).
 */
export default function LeadActivityTable() {
  const { bounds } = usePeriodFilter();
  const { data: rows, isLoading } = useLeadStats(bounds);
  const { data: users = [] } = useUsers();
  const names = new Map(users.map((u) => [u.id, u.full_name]));
  const perUser = summarizeByUser(rows ?? [], new Date(), bounds).sort((a, b) => (names.get(a.user_id) ?? "").localeCompare(names.get(b.user_id) ?? "", "nb"));

  return (
    <section aria-label="Ringeaktivitet" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-semibold">Ringeaktivitet</h2>
        <PeriodSelector />
      </div>

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : perUser.length === 0 ? (
        <p className="border border-border p-6 text-sm text-muted-foreground">Ingen samtaler i perioden.</p>
      ) : (
        <div className="overflow-x-auto border-[1.5px] border-foreground">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th rowSpan={2} className="p-2 text-left">Møtebooker</th>
                {["I dag", "Denne uken", "Valgt periode"].map((g) => (
                  <th key={g} colSpan={6} className="border-l p-2 text-center">{g}</th>
                ))}
              </tr>
              <tr className="border-b text-xs text-muted-foreground">
                {[0, 1, 2].flatMap((g) =>
                  ["Samtaler", "Svar", "Svarandel", "Møter booket", "Møter per samtale", "Gjennomført"].map((h, i) => (
                    <th key={`${g}-${h}`} className={`p-2 text-right font-normal ${i === 0 ? "border-l" : ""}`}>{h}</th>
                  )),
                )}
              </tr>
            </thead>
            <tbody>
              {perUser.map((u) => (
                <tr key={u.user_id} className="border-b last:border-b-0">
                  <th scope="row" className="p-2 text-left font-medium">{names.get(u.user_id) ?? "Ukjent"}</th>
                  <Cells t={u.today} />
                  <Cells t={u.week} />
                  <Cells t={u.period} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </section>
  );
}
